/**
 * ELF-CIRCLE — Where We Stand on AI (design artboard `#5c`).
 *
 * Film band with the headline, the positioning statement, then the three GEO
 * principles as ghost-numbered cards.
 *
 * Static page — imagery is a bundled asset rather than an editable CMS slot.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import bandImage from '../../../../../assets/elf/ai-geo-handshake.jpg';

const PRINCIPLES = [
  { num: '01', name: 'Structured', body: 'Content built to be parsed, not just read.' },
  { num: '02', name: 'Citable', body: 'Claims an answer engine can quote and attribute.' },
  { num: '03', name: 'Brand-consistent', body: 'The same voice, wherever the answer surfaces.' },
];

export const AiGeo: React.FC = () => {
  const consult = useConsult();
  return (
  <ElfShell>
    <div className="elf-page">
      <div className="elf-page__panel">
        <div className="elf-page__bleed" style={{ position: 'relative', height: 500, overflow: 'hidden' }}>
          <img className="bandimg bandimg--colour" src={bandImage} alt="" />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top,rgba(0,0,0,.92) 4%,rgba(0,0,0,.42) 52%,rgba(0,0,0,.62))',
            }}
          />
          <div
            className="elf-page__col"
            style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <ElfPageNav current="/ai-and-geo" onFilm />
            <div className="wnav-onfilm" style={{ padding: '22px var(--pgut) 0' }}>
              <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'AI & GEO']} />
            </div>
            <div
              style={{ marginTop: 'auto', padding: '0 var(--pgut) 48px', display: 'flex', flexDirection: 'column', gap: 18 }}
            >
              <span
                className="eyebrow"
                style={{
                  alignSelf: 'flex-start',
                  borderColor: 'rgba(255,255,255,.28)',
                  background: 'rgba(255,255,255,.1)',
                  color: 'rgba(255,255,255,.8)',
                }}
              >
                <i />
                Where we stand
              </span>
              <h1
                className="elf-page__h1 elf-page__h1--film"
                style={{ fontSize: 'clamp(48px,5.4vw,80px)', maxWidth: '17ch' }}
              >
                <span className="mask">
                  <span>We treat GEO as</span>
                </span>
                <span className="mask">
                  <span style={{ animationDelay: '.12s' }}>
                    infrastructure, <span style={{ color: 'var(--acc-hero)' }}>not a trend.</span>
                  </span>
                </span>
              </h1>
              <p
                style={{
                  margin: 0,
                  font: "400 15px/1.6 'Space Mono',monospace",
                  color: 'rgba(255,255,255,.72)',
                  maxWidth: '62ch',
                }}
              >
                Structured, citable, brand-consistent content — built to be legible to answer
                engines from day one.
              </p>
            </div>
          </div>
        </div>

        <div style={{ padding: '52px var(--pgut) 26px' }}>
          <p
            style={{
              margin: 0,
              font: "400 clamp(19px,1.6vw,23px)/1.62 'Archivo',sans-serif",
              color: 'rgba(var(--panel-fg-rgb),.82)',
              textWrap: 'pretty',
              maxWidth: 1080,
            }}
          >
            Search is no longer a list of blue links — it&rsquo;s an answer. AI Overviews,
            generative assistants, and recommendation engines are becoming the front door to
            discovery, and most brands&rsquo; web presence wasn&rsquo;t built to be legible to
            them. ELF-CIRCLE builds and markets with that shift in view from day one: structured,
            citable, brand-consistent content that performs in traditional search and shows up
            correctly inside AI-generated answers. We call it GEO — Generative Engine Optimization
            — and we treat it as infrastructure, not a trend.
          </p>
        </div>

        <div
          className="elf-page__grid3"
          style={{ padding: '20px var(--pgut) 56px', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}
        >
          {PRINCIPLES.map((p) => (
            <div className="chap" key={p.num}>
              <span className="chapghost">{p.num}</span>
              <span className="colhead">Principle</span>
              <span className="stagehead" style={{ fontSize: 27 }}>
                {p.name}
              </span>
              <span className="wbar" />
              <p className="wbody" style={{ fontSize: 15 }}>
                {p.body}
              </p>
            </div>
          ))}
        </div>

        <div
          className="elf-page__grid2"
          style={{
            padding: '0 var(--pgut) 72px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
          }}
        >
          <Link className="nextlink" to={route('/portfolio')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="statl">Next</span>
              <span className="whead" style={{ fontSize: 20 }}>
                Signature work
              </span>
              <span className="wtag" style={{ fontSize: 12.5 }}>
                Music, film and live production.
              </span>
            </div>
            <span className="arw">→</span>
          </Link>
          <button
              type="button"
              className="nextlink"
              onClick={() => consult?.open({ source: 'aigeo.proposal', interest: 'AI & GEO', title: 'Request a proposal' })}
            ><div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span className="statl">Start here</span>
              <span className="whead" style={{ fontSize: 20 }}>
                Request a proposal
              </span>
              <span className="wtag" style={{ fontSize: 12.5 }}>
                Ask what AI answers say about your brand today.
              </span>
            </div>
            <span className="arw">→</span>
          </button>
        </div>
      </div>
    </div>
    <ElfFooter />
  </ElfShell>
  );
};

export default AiGeo;
